/* RBI EDF form numbers: upload the Excel from the RBI site; each new EDF takes the next unused number once. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Download, Hash, Loader2, Upload } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

export const EdfNumbersCard = ({ onChanged }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = useCallback(() => axios.get(`${API}/edf-doc/numbers/summary`, { headers: getAuthHeader() }).then(r => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = (await axios.post(`${API}/edf-doc/numbers/upload`, fd, { headers: getAuthHeader() })).data;
      toast.success(`${r.added} new EDF numbers added${r.skipped_duplicates ? ` · ${r.skipped_duplicates} skipped (already in list / used)` : ""}${r.filled_waiting ? ` · ${r.filled_waiting} waiting EDFs numbered` : ""}`);
      setS(r); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read this file"); }
    setBusy(false); if (ref.current) ref.current.value = "";
  };
  if (!s) return null;
  const downloadRegister = async () => {
    try {
      const r = await axios.get(`${API}/edf-doc/numbers/register.xlsx`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "EDF Number Register.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Could not download the register"); }
  };
  return (
    <div className={`rounded-xl border p-4 ${s.low ? "border-rose-300 bg-rose-50" : "bg-white"}`} data-testid="edf-numbers-card">
      <div className="flex flex-wrap items-center gap-3">
        <Hash className="h-5 w-5 text-indigo-600" />
        <b className="text-sm text-slate-800">RBI EDF form numbers</b>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${s.low ? "bg-rose-600 text-white" : "bg-emerald-100 text-emerald-800"}`} data-testid="edf-numbers-free">{s.free} left</span>
        <span className="text-xs text-slate-500" data-testid="edf-numbers-used">{s.used} used</span>
        {s.next && <span className="text-xs text-slate-500">Next: <b className="font-mono text-slate-800" data-testid="edf-numbers-next">{s.next}</b></span>}
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="edf-numbers-input" />
        <Button size="sm" variant="outline" className="h-8" onClick={downloadRegister} data-testid="edf-numbers-register">
          <Download className="mr-1 h-4 w-4" /> Number register (Excel)</Button>
        <Button size="sm" variant="outline" className="ml-auto h-8" disabled={busy} onClick={() => ref.current?.click()} data-testid="edf-numbers-upload">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Upload RBI numbers (Excel)</Button>
      </div>
      {s.low && <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-rose-700" data-testid="edf-numbers-low">
        <AlertTriangle className="h-4 w-4" /> Only {s.free} numbers left — download more from the RBI site and upload them here.</p>}
      {s.waiting > 0 && <p className="mt-1 text-xs text-amber-700" data-testid="edf-numbers-waiting">{s.waiting} EDF form(s) waiting for a number — they get one automatically after the next upload.</p>}
      <p className="mt-1 text-[11px] text-slate-500">One number is used once only: it prints as Form No and RBI approval no., stays locked to that invoice and is never given out again — even if the EDF is deleted.</p>
    </div>
  );
};
